import React, { useEffect, useState } from "react";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { getCurrentUser } from "../../../lib/authSession";
import {
  confirmTwoFactorEnable,
  disableTwoFactor,
  fetchTwoFactorStatus,
  requestTwoFactorEnable,
} from "../../../lib/twoFactorApi";
import { useTranslation } from "../../../i18n";

/**
 * Real two-factor block for the Settings page.
 * Flow: enable → code emailed → confirm code. Disable requires the password.
 */
const TwoFactorCard = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(null); // null = loading
  const [step, setStep] = useState("idle"); // idle | enterCode | enterPassword
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [devMode, setDevMode] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const status = await fetchTwoFactorStatus();
        if (active) setEnabled(Boolean(status.enabled));
      } catch {
        if (active) setEnabled(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const email = getCurrentUser()?.email || "";

  const startEnable = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await requestTwoFactorEnable();
      setDevMode(Boolean(res?.dev_mode));
      setStep("enterCode");
      setCode("");
    } catch (err) {
      // Map known failure codes to localized copy; fall back to backend detail.
      const code = err?.data?.code;
      const byCode = {
        email_send_failed: t("settings.twoFactorEmailFailed"),
        email_not_configured: t("settings.twoFactorEmailNotConfigured"),
      };
      setError(byCode[code] || err?.data?.detail || t("settings.twoFactorError"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError("");
    try {
      await confirmTwoFactorEnable(code);
      setEnabled(true);
      setStep("idle");
    } catch {
      setError(t("settings.twoFactorCodeInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      await disableTwoFactor(password);
      setEnabled(false);
      setStep("idle");
      setPassword("");
    } catch {
      setError(t("settings.twoFactorPasswordInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setStep("idle");
    setCode("");
    setPassword("");
    setError("");
  };

  return (
    <div className="rounded-lg bg-surface p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-secondary-container">
            <MaterialIcon name="verified_user" className="text-[20px] text-secondary" />
          </div>
          <div>
            <p className="font-body-md font-semibold text-on-surface">
              {t("settings.twoFactor")}
            </p>
            <p className="text-label-sm text-on-surface-variant">
              {t("settings.twoFactorHint")}
            </p>
          </div>
        </div>

        {enabled === null ? null : step !== "idle" ? null : enabled ? (
          <div className="flex items-center gap-3 self-start sm:self-center">
            <span className="rounded-full bg-secondary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-secondary">
              {t("common.enabled")}
            </span>
            <button
              type="button"
              onClick={() => setStep("enterPassword")}
              className="rounded-lg border border-outline-variant px-3 py-1.5 text-label-sm text-on-surface-variant transition hover:bg-surface-container"
            >
              {t("settings.twoFactorDisable")}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 self-start sm:self-center">
            <span className="rounded-full bg-surface-container-high px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              {t("common.disabled")}
            </span>
            <button
              type="button"
              onClick={startEnable}
              disabled={busy}
              className="rounded-lg bg-primary px-3 py-1.5 text-label-sm font-medium text-on-primary transition hover:bg-primary/90 disabled:opacity-60"
            >
              {busy ? t("settings.twoFactorSending") : t("settings.twoFactorEnable")}
            </button>
          </div>
        )}
      </div>

      {step === "idle" && error ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-error-container px-3 py-2.5 text-label-sm text-on-error-container" role="alert">
          <MaterialIcon name="warning" className="mt-0.5 shrink-0 text-[16px]" />
          {error}
        </p>
      ) : null}

      {step === "enterCode" ? (
        <div className="mt-4 rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-4">
          {devMode ? (
            <p className="mb-3 flex items-start gap-2 rounded-lg bg-yellow-500/10 px-3 py-2 text-label-sm text-yellow-800">
              <MaterialIcon name="info" className="mt-0.5 shrink-0 text-[16px]" />
              {t("settings.twoFactorDevMode")}
            </p>
          ) : (
            <p className="mb-3 text-label-sm text-on-surface-variant">
              {t("settings.twoFactorEnterCode", { email })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              autoFocus
              className="w-36 rounded-lg border border-outline-variant bg-white px-3 py-2 text-center text-[16px] font-semibold tracking-[6px] outline-none focus:ring-2 focus:ring-secondary"
            />
            <button
              type="button"
              onClick={confirmEnable}
              disabled={busy || code.length !== 6}
              className="rounded-lg bg-primary px-4 py-2 text-label-sm font-medium text-on-primary disabled:opacity-50"
            >
              {t("settings.twoFactorConfirm")}
            </button>
            <button
              type="button"
              onClick={cancel}
              className="rounded-lg px-3 py-2 text-label-sm text-on-surface-variant hover:bg-surface-container"
            >
              {t("common.cancel")}
            </button>
          </div>
          {error ? <p className="mt-2 text-label-sm text-error">{error}</p> : null}
        </div>
      ) : null}

      {step === "enterPassword" ? (
        <div className="mt-4 rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-4">
          <p className="mb-3 text-label-sm text-on-surface-variant">
            {t("settings.twoFactorPasswordPrompt")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoFocus
              className="w-52 rounded-lg border border-outline-variant bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-secondary"
            />
            <button
              type="button"
              onClick={confirmDisable}
              disabled={busy || !password}
              className="rounded-lg bg-error px-4 py-2 text-label-sm font-medium text-white disabled:opacity-50"
            >
              {t("settings.twoFactorDisable")}
            </button>
            <button
              type="button"
              onClick={cancel}
              className="rounded-lg px-3 py-2 text-label-sm text-on-surface-variant hover:bg-surface-container"
            >
              {t("common.cancel")}
            </button>
          </div>
          {error ? <p className="mt-2 text-label-sm text-error">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
};

export default TwoFactorCard;
